import React, { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import MediaRelationsService from '../../services/mediarelations.services';
import { useCompanyId } from '../../services/CompanyidProvider';
import { Stack, Box, LinearProgress, Modal } from '@mui/material';
import { FluxButton } from '@meltwater/flux-web-components-react';
import csvDownload from 'json-to-csv-export';
import styles from './mediarelations.module.css';
import { getJwtToken } from '../../services/jwt.services';
import jwt_decode from 'jwt-decode';
import { useMediaRelationsData } from './mediarelations';
import DataTable from '../../components/DataTable';

const defaultOrg = {organization:{name:""}, newswireOrganizationId:0};

const NewswireOrgs = ({ load, setActiveTab }) => {
    const [newswireOrgToDelete, setNewswireOrgToDelete] = useState(defaultOrg);
    const [mediarelationspermissions, setMediarelationspermissions] = useState([]);
    const [deleteOrgModalOpen, setDeleteOrgModalOpen] = useState(false);
    const [deleteOrgButtonEnabled, setDeleteOrgButtonEnabled] = useState(false);
    const [alertSeverity, setAlertSeverity] = useState('info');
    const [openSnackBar, setOpenSnackBar] = useState(false);
    const [feedbackMsg, setFeedbackMsg] = useState('');
    const [isFetching, setIsFetching] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [loadingMessage, setLoadingMessage] = useState('');
    const [hasError, setHasError] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    
    const { companyId } = useCompanyId();
    const { 
        newswireOrgsData, 
        setNewswireOrgsData,
        setLoading,
        apiAttempts,
        markApiAttempt,
        resetApiAttempt
    } = useMediaRelationsData();
    
    const loadingTimerRef = useRef(null);
    const landingPageNavRef = useRef(false);
    const isRequestInProgressRef = useRef(false);
    const isMountedRef = useRef(true);
    
    // Custom guide steps specific to NewswireOrgs
    const newswireOrgsGuideSteps = useMemo(() => [
        {
            title: 'Newswire Organizations',
            text: 'This guide will help you navigate the Newswire Organizations features.',
            element: null,
        },
        {
            title: 'Search Organizations',
            text: 'Use the search box to quickly find newswire organizations by name or ID.',
            element: '.mrt-input',
            position: { top: 20, left: 200 }
        },
        {
            title: 'Column Visibility & Table Settings',
            text: 'Use these buttons to show or hide specific columns and adjust table settings.',
            element: '.mrt-button.mrt-dropdown-button',
            position: { top: 0, right: 80 }
        },
        {
            title: 'Export Organizations',
            text: 'You can export all newswire organizations data using this button.',
            element: '.MuiButtonBase-root.FluxButton-root',
            position: { top: 0, left: 100 }
        },
        {
            title: 'Delete Organization',
            text: 'If you have admin permissions, you can delete organizations using the Delete button.',
            element: 'button.MuiButtonBase-root.FluxButton-root:nth-of-type(2)',
            position: { top: 100, left: 850 }
        },
        {
            title: 'Refresh Data',
            text: 'Use this button to refresh organization data from the server.',
            element: 'button[title="Refresh Data"]',
            position: { top: 0, left: 300 }
        },
    ], []);

    const handleOpenDeleteOrgModal = useCallback(async (org) => {
        setNewswireOrgToDelete(org);
        setDeleteOrgModalOpen(true);
        setTimeout(() => { setDeleteOrgButtonEnabled(true) }, 6000);
    }, []);

    // Track initial load to identify direct navigation from landing page
    useEffect(() => {
        if (load && !landingPageNavRef.current) {
            console.log("Newswire Orgs tab activated first time from landing page");
            landingPageNavRef.current = true;
        }
    }, [load]);

    // Fetch data function separated for reuse
    const fetchData = useCallback(async () => {
        // Prevent multiple simultaneous requests
        if (isRequestInProgressRef.current) {
            console.log("Request already in progress, skipping duplicate call");
            return;
        }

        try {
            isRequestInProgressRef.current = true;
            setIsLoading(true);
            setLoading(true);
            setLoadingMessage('Fetching newswire organizations...');
            loadingTimerRef.current = setTimeout(() => {
                if (isMountedRef.current) {
                    setLoadingMessage('Please wait, it takes longer than usual for larger companies...');
                }
            }, 15000);
            
            console.log("Initiating getNewswireOrgs call for company:", companyId);
            const response = await MediaRelationsService.getNewswireOrgs(companyId);
            console.log("getNewswireOrgs call successful");
            
            // Only update state if component is still mounted
            if (isMountedRef.current) {
                setNewswireOrgsData(response.data);
                setFeedbackMsg(`Successfully loaded ${response.data.length} newswire organizations.`);
                setAlertSeverity('success');
                setOpenSnackBar(true);
                setHasError(false);
                
                // Mark that we've successfully fetched data
                markApiAttempt('newswireorgs');
            }
            
            return response.data;
        } catch (error) {
            console.error("Error in getNewswireOrgs API call:", error);
            
            // Only update state if component is still mounted
            if (isMountedRef.current) {
                setOpenSnackBar(true);
                const errorMsg = error.message || 'Unknown error';
                setFeedbackMsg(`Error loading data: ${errorMsg}, Please refresh or contact support.`);
                setErrorMessage(errorMsg);
                setAlertSeverity('error');
                setHasError(true);
            }
            throw error;
        } finally {
            isRequestInProgressRef.current = false;
            if (loadingTimerRef.current) {
                clearTimeout(loadingTimerRef.current);
                loadingTimerRef.current = null;
            }
            if (isMountedRef.current) {
                setLoadingMessage('');
                setIsLoading(false);
                setLoading(false);
            }
        }
    }, [companyId, setLoading, setNewswireOrgsData, markApiAttempt]);

    // Effect for initial setup
    useEffect(() => {
        const jwtToken = getJwtToken();
        setIsFetching(false);
        
        if (jwtToken) {
            const { mediarelationspermissions } = jwt_decode(jwtToken);
            setMediarelationspermissions(mediarelationspermissions);
        }

        // Track component mount status
        isMountedRef.current = true;
        
        return () => {
            isMountedRef.current = false;
            isRequestInProgressRef.current = false;
            if (loadingTimerRef.current) {
                clearTimeout(loadingTimerRef.current);
                loadingTimerRef.current = null;
            }
        };
    }, []);

    // Separate effect for data loading
    useEffect(() => {
        if (!load || !companyId) {
            return;
        }

        async function loadData() {
            // Check if we have cached data
            if (newswireOrgsData) {
                console.log("Using cached newswire orgs data");
                setIsLoading(false);
                return;
            }
            
            // Check if this component has already attempted to load data and had an error
            if (hasError && apiAttempts.newswireorgs) {
                console.log("Skipping API call due to previous error");
                return;
            }
            
            // Prevent multiple requests
            if (isRequestInProgressRef.current) {
                console.log("Request already in progress, skipping");
                return;
            }
            
            // Fetch new data if needed
            try {
                console.log("No cached data found, fetching fresh data");
                await fetchData();
            } catch (error) {
                console.error("Error loading newswire organizations data:", error);
            }
        }
        
        loadData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [load, companyId, newswireOrgsData, hasError, apiAttempts.newswireorgs]);

    const resetErrorState = () => {
        console.log("Resetting newswire orgs error state to allow retry");
        setHasError(false);
        resetApiAttempt('newswireorgs'); // Reset the API attempt tracking
    };

    const refreshData = async () => {
        // Prevent multiple refresh calls
        if (isRequestInProgressRef.current) {
            console.log("Refresh already in progress, skipping");
            return;
        }

        try {
            resetApiAttempt('newswireorgs'); // Reset API attempt for refresh
            setHasError(false); // Clear any previous errors
            setLoadingMessage('Refreshing newswire organizations...');
            await fetchData();
            if (isMountedRef.current) {
                setFeedbackMsg('Data refreshed successfully');
                setAlertSeverity('success');
                setOpenSnackBar(true);
            }
        } catch (error) {
            console.error("Error refreshing newswire organizations:", error);
            if (isMountedRef.current) {
                setHasError(true); // Track error state
                const errorMsg = error.message || 'Unknown error';
                setErrorMessage(errorMsg);
                setFeedbackMsg(`Error refreshing data: ${errorMsg}, please try again or contact support.`);
                setAlertSeverity('error');
                setOpenSnackBar(true);
            }
        }
    };

    const modalStyle = {
        backgroundColor: '#ffffff',
        padding: '20px',
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: "30%",
        boxShadow: 24,
        p: 4,
        height: "auto",
    };

    const handleCloseDeleteOrgModal = () => {
        setDeleteOrgModalOpen(false);
        setNewswireOrgToDelete(defaultOrg);
        setDeleteOrgButtonEnabled(false);
    };

    const handleDeleteOrg = async () => {
        handleCloseSnackBar();
        setIsFetching(true);
        try {
            await MediaRelationsService.deleteNewswireOrg(companyId, newswireOrgToDelete._id);
            setFeedbackMsg(`Deleted newswire org "${newswireOrgToDelete.organization.name}"`);
            setAlertSeverity("success");
            setOpenSnackBar(true);
            handleCloseDeleteOrgModal();
            // Trigger a refresh after successful delete
            setTimeout(() => {
                setNewswireOrgsData(null);
                resetApiAttempt('newswireorgs'); // Ensure we'll fetch fresh data
            }, 1500);
        }
        catch (error) {
            console.error(`Error deleting newswire org "${newswireOrgToDelete.organization.name}. ${error.message}`);
            setFeedbackMsg(`Error deleting newswire org "${newswireOrgToDelete.organization.name}. ${error.message}`);
            setAlertSeverity("error");
            setOpenSnackBar(true);
        }
        setIsFetching(false);
    };

    const handleCloseSnackBar = () => {
        setOpenSnackBar(false);
        setFeedbackMsg('');
        setAlertSeverity("info");
    };

    const exportNewswireOrgs = (rows) => {
        const exportdata = rows.map((row) => {
            row = row.original;
            return {
                id: row._id,
                newswireOrganizationId: row.newswireOrganizationId,
                orgName: row.organization.name,
                orgAddressLine1: row.organization.addressLine1,
                orgAddressLine2: row.organization.addressLine2,
                orgCity: row.organization.city,
                orgCountry: row.organization.country,
                orgExchangeName: row.organization.exchangeName,
                orgType: row.organization.orgType,
                orgPostalCode: row.organization.postalCode,
                orgState: row.organization.state,
                orgTickerSymbol: row.organization.tickerSymbol,
                orgWebsite: row.organization.website,
                contactName: row.contact?.name,
                contactEmail: row.contact?.email,
                contactPhone: row.contact?.phone,
                createdAt: row.createdAt,
                createdBy: row.createdBy
            };
        });

        const data = {
            data: exportdata,
            delimiter: ',',
            filename: `Newswire Orgs for ${companyId}`,
        };

        csvDownload(data);
    };

    const renderToolbarActions = ({ table }) => {
        return (
            <Stack direction="row" spacing={1} alignItems="center">
                <FluxButton
                    tint="primary"
                    type="filled"
                    onClick={() => exportNewswireOrgs(table.getPrePaginationRowModel().rows)}
                >
                    Export Newswire Orgs
                </FluxButton>
                <FluxButton
                    tint="secondary"
                    type="filled"
                    onClick={refreshData}
                >
                    Refresh Data
                </FluxButton>
            </Stack>
        );
    };

    const columns = useMemo(
        () => [
            {
                header: 'Org Name',
                accessorKey: 'organization.name',
                enableGlobalFilter: true
            },
            {
                header: 'id',
                accessorKey: '_id',
                enableGlobalFilter: true
            },
            {
                header: 'newswireOrganizationId',
                accessorKey: 'newswireOrganizationId',
                enableGlobalFilter: true
            },
            {
                header: 'delete',
                accessorFn: (row) => `${row._id}`,
                Cell: ({ row }) => (
                    <FluxButton
                        tint="accent"
                        type="filled"
                        onClick={() => { handleOpenDeleteOrgModal(row.original); }}
                    >
                        Delete
                    </FluxButton>
                ),
                enableGlobalFilter: false
            }
        ],
        [handleOpenDeleteOrgModal],
    );

    if (!mediarelationspermissions || !mediarelationspermissions.includes('read')) {
        return <div>You do not have permission to access this page.</div>;
    }

    return (
        <div className={styles.Search}>
            {isFetching && (
                <Box sx={{ width: '100%' }}>
                    <LinearProgress color="secondary" />
                </Box>
            )}

            <DataTable 
                columns={columns}
                data={newswireOrgsData || []}
                isLoading={isLoading && !hasError}
                loadingMessage={loadingMessage || 'Loading newswire organizations...'}
                hasError={hasError}
                errorMessage={errorMessage}
                errorTitle="Error Loading Newswire Organizations"
                onBackClick={() => setActiveTab(null)}
                onTryAgain={() => {
                    resetErrorState();
                    refreshData();
                }}
                renderToolbarActions={renderToolbarActions}
                backTooltip="Return to Dashboard"
                showGuide={false}
                guideSteps={newswireOrgsGuideSteps}
                snackbarProps={{
                    open: openSnackBar,
                    onClose: handleCloseSnackBar,
                    message: feedbackMsg,
                    severity: alertSeverity,
                    autoHideDuration: 10000
                }}
                tableProps={{
                    muiSearchTextFieldProps: {
                        placeholder: 'Search newswire orgs...',
                        variant: 'standard',
                        sx: { width: '300px' }
                    },
                    initialState: { 
                        density: 'compact', 
                        columnVisibility: { 
                            "delete": mediarelationspermissions.includes('admin')
                        }, 
                        pagination: { pageSize: 50 }
                    }
                }}
            />

            <Modal
                open={deleteOrgModalOpen}
                onClose={handleCloseDeleteOrgModal}
                aria-labelledby='modal-modal-title'
                aria-describedby='modal-modal-description'
            >
                <Box style={modalStyle}>
                    <Stack direction='row' spacing={4} justifyContent='flex-end' alignContent={"right"}>
                        <FluxButton onClick={handleCloseDeleteOrgModal} type='text'>
                            Cancel
                        </FluxButton>
                    </Stack>
                    <Stack direction='column' spacing={1} padding={2} justifyContent='flex-start' alignItems='flex-start'>
                        <div>
                            Are you sure you want to delete org {newswireOrgToDelete.organization.name}? Please contact GlobeNewswire at <a href='mailto:support@globenewswire.com'>support@globenewswire.com</a> to ask them to delete the same newswire organization on their end if you have not done so yet.<br/><br/>
                            Newswire Organization ID: {newswireOrgToDelete.newswireOrganizationId}
                        </div>
                    </Stack>
                    <Stack direction='row' paddingTop={2} spacing={4} justifyContent='flex-end' alignItems='flex-end'>
                        <FluxButton tint="primary" type="filled" onClick={handleDeleteOrg} disabled={!deleteOrgButtonEnabled}>
                            Delete
                        </FluxButton>
                    </Stack>
                </Box>
            </Modal>
        </div>
    );
};

export default NewswireOrgs;
